import React from 'react';
import Icon from '../../../components/AppIcon';

const RequiredServicesStats = ({ deploymentPoints }) => {
  const calculateStats = () => {
    const totalRequired = deploymentPoints.reduce((sum, point) => sum + point?.reqCount || 0, 0);
    const totalAllocated = deploymentPoints.reduce((sum, point) => sum + point?.allocatedCount || 0, 0);
    const totalShortage = totalRequired - totalAllocated;
    const fillRate = totalRequired > 0 ? Math.round((totalAllocated / totalRequired) * 100) : 0;

    const criticalPoints = deploymentPoints.filter(point => 
      (point.allocatedCount / point.reqCount) < 0.5
    ).length;

    const fullyAllocated = deploymentPoints.filter(point => 
      point.reqCount > 0 && point.allocatedCount >= point.reqCount
    ).length;

    return {
      totalRequired,
      totalAllocated,
      totalShortage,
      fillRate,
      criticalPoints,
      fullyAllocated,
      totalPoints: deploymentPoints.length
    };
  };

  const stats = calculateStats();

  const statCards = [
    {
      title: 'Total Required',
      value: stats.totalRequired,
      icon: 'Target',
      color: 'primary',
      description: 'Total positions needed'
    },
    {
      title: 'Total Allocated',
      value: stats.totalAllocated,
      icon: 'Users',
      color: 'success',
      description: 'Members assigned'
    },
    {
      title: 'Shortfall',
      value: stats.totalShortage,
      icon: 'AlertTriangle',
      color: 'error',
      description: 'Positions unfilled'
    },
    {
      title: 'Fill Rate',
      value: `${stats.fillRate}%`,
      icon: 'TrendingUp',
      color: stats.fillRate >= 80 ? 'success' : stats.fillRate >= 60 ? 'warning' : 'error',
      description: 'Overall allocation rate'
    },
    {
      title: 'Points',
      value: stats.criticalPoints,
      icon: 'AlertCircle',
      color: 'error',
      description: 'Points with &lt;50% allocation'
    },
    {
      title: 'Fully Allocated',
      value: stats.fullyAllocated,
      icon: 'CheckCircle',
      color: 'success',
      description: 'Points at 100% capacity'
    }
  ];

  const getCardStyles = (color) => {
    const styles = {
      primary: {
        bg: 'bg-primary/5',
        border: 'border-primary/20',
        icon: 'text-primary',
        value: 'text-primary'
      },
      success: {
        bg: 'bg-success/5',
        border: 'border-success/20',
        icon: 'text-success',
        value: 'text-success'
      },
      warning: {
        bg: 'bg-warning/5',
        border: 'border-warning/20',
        icon: 'text-warning',
        value: 'text-warning'
      },
      error: {
        bg: 'bg-error/5',
        border: 'border-error/20',
        icon: 'text-error',
        value: 'text-error'
      }
    };
    return styles[color] || styles.primary;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
      {statCards.map((card) => {
        const cardStyles = getCardStyles(card.color);
        
        return (
          <div
            key={card.title}
            className={`
              ${cardStyles.bg} ${cardStyles.border}
              bg-card border rounded-lg p-4 
              hover:shadow-spiritual-md transition-all duration-300
            `}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`
                w-10 h-10 rounded-lg flex items-center justify-center
                ${cardStyles.bg} ${cardStyles.border}
              `}>
                <Icon 
                  name={card.icon} 
                  size={20} 
                  className={cardStyles.icon}
                />
              </div>
            </div>

            <div className="space-y-1">
              <div className={`text-2xl font-heading font-semibold ${cardStyles.value}`}>
                {card.value}
              </div>
              
              <h4 className="font-heading font-medium text-foreground text-sm">
                {card.title}
              </h4>
              
              <p className="text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: card.description }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RequiredServicesStats;
